<template>
  <div class="flex items-center justify-center p-0 w-full"
      @dragenter.prevent="onDragEnter"
      @dragleave.prevent="onDragLeave"
      @dragover.prevent="onDragOver"
      @drop.prevent="onDrop">
    <!-- Drag Overlay -->
    <transition
      enter-active-class="transition ease-out duration-200"
      enter-from-class="opacity-0 scale-95"
      enter-to-class="opacity-100 scale-100"
      leave-active-class="transition ease-in duration-150"
      leave-from-class="opacity-100 scale-100"
      leave-to-class="opacity-0 scale-95"
    >
      <div 
        v-if="isDragging"
        class="fixed inset-0 z-[100] bg-[#2EB2A0]/95 backdrop-blur-sm flex flex-col items-center justify-center pointer-events-none"
      >
        <div class="bg-white/20 p-8 rounded-full mb-8 backdrop-blur-sm shadow-2xl">
           <span class="material-symbols-outlined text-white text-[100px]">call_merge</span>
        </div>
        <h2 class="text-white text-5xl font-bold tracking-tight drop-shadow-sm">Drop PDF files to Merge</h2>
      </div>
    </transition>

    <!-- Dropzone -->
    <div 
      v-if="pages.length === 0" 
      @click="fileInput.click()" 
      class="w-full max-w-[1000px] mt-4 transition-all duration-300 cursor-pointer flex flex-col items-center justify-center text-center relative group p-0"
    >
      <div class="absolute -inset-1 bg-gradient-to-r from-[#2EB2A0] to-[#2EB2A0]/60 rounded-2xl blur opacity-10 group-hover:opacity-25 transition duration-1000 group-hover:duration-200"></div>
      <div class="relative w-full h-[360px] bg-white border-2 border-dashed border-slate-300 rounded-2xl flex flex-col items-center justify-center gap-6 transition-all hover:border-[#2EB2A0]/50 p-6">
        <div class="size-20 bg-[#2EB2A0]/10 rounded-full flex items-center justify-center text-[#2EB2A0] mt-2">
           <span class="material-symbols-outlined text-[48px]">call_merge</span>
        </div>
        
        <div class="space-y-1">
            <h3 class="text-2xl font-bold text-[#16304D]">Select or Drop PDF Files to Merge</h3>
            <p class="text-sm font-medium text-slate-500">Combine multiple PDF documents into a single file in seconds</p>
        </div>

        <button @click.stop="fileInput.click()" class="bg-[#2EB2A0] text-white text-base font-semibold px-8 py-3 rounded-xl hover:bg-[#279B8C] shadow-xl shadow-[#2EB2A0]/20 transition-all duration-300 ease-in-out transform hover:-translate-y-0.5 flex items-center gap-2">
            <span class="material-symbols-outlined">add_circle</span>
            <span>Select PDF Files</span>
        </button>

        <div class="flex flex-wrap justify-center items-center gap-4 text-[10px] font-semibold text-slate-400 uppercase tracking-widest mt-1">
             <span class="flex items-center gap-1.5"><span class="material-symbols-outlined text-[14px]">lock</span> 100% Secure &amp; Client-side</span>
             <span class="flex items-center gap-1.5"><span class="material-symbols-outlined text-[14px]">verified_user</span> Multi-File Support</span>
             <span class="flex items-center gap-1.5"><span class="material-symbols-outlined text-[14px]">cloud_done</span> Free &amp; Unlimited</span>
        </div>
      </div>
    </div>

    <!-- Main Merge Editor Workspace -->
    <div v-else class="fixed inset-0 top-0 z-50 flex flex-col h-screen bg-[#F3F4F6]">
        <!-- Top Navigation Bar -->
        <header class="flex items-center justify-between whitespace-nowrap border-b border-slate-200 bg-white px-4 md:px-6 py-3 shrink-0 shadow-sm z-20">
            <div class="flex items-center gap-3 md:gap-4">
                <a href="/" class="flex items-center gap-2">
                     <img :src="'/logo.png'" alt="Logo" class="h-8 md:h-10 w-auto" @error="(e) => e.target.style.display='none'">
                     <span class="font-bold text-[#16304D] text-lg hidden sm:inline">Merge PDF</span>
                </a>
                <div class="h-6 w-px bg-slate-200 hidden sm:block"></div>
                <div>
                    <div class="flex items-center gap-2 text-xs md:text-sm text-slate-600 font-medium">
                        <span>{{ fileInfo.fileCount }} {{ fileInfo.fileCount === 1 ? 'PDF' : 'PDFs' }}</span>
                        <span class="text-slate-300">&middot;</span>
                        <span class="font-bold text-[#2EB2A0]">{{ pages.length }} Total Pages</span>
                    </div>
                </div>
            </div>
            <div class="flex items-center gap-2 md:gap-4">
                 <button @click="addMoreFiles" class="flex items-center justify-center gap-2 rounded-xl h-10 px-4 bg-[#2EB2A0]/10 text-[#2EB2A0] border border-[#2EB2A0]/30 text-sm font-semibold hover:bg-[#2EB2A0]/20 transition-colors">
                    <span class="material-symbols-outlined text-lg">add_box</span>
                    <span class="hidden md:inline">Add More PDFs</span>
                </button>
                <button @click="resetAll" class="flex items-center justify-center gap-2 rounded-xl h-10 px-4 bg-red-50 border border-red-200 text-sm font-semibold hover:bg-red-100 text-red-600 transition-colors">
                    <span class="material-symbols-outlined text-lg">restart_alt</span>
                    <span class="hidden md:inline">Start Over</span>
                </button>
            </div>
        </header>

        <div class="flex flex-1 overflow-hidden relative">
            <!-- Main Grid Workspace -->
            <main ref="scrollContainer" class="flex-1 overflow-y-auto bg-[#F3F4F6] p-4 md:p-8 relative">
                <div class="max-w-7xl mx-auto pb-24">
                    <!-- Files Badges Header -->
                    <div class="mb-6 flex flex-wrap gap-2 items-center bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                        <span class="text-xs font-bold uppercase tracking-wider text-slate-400 mr-2">Loaded Files:</span>
                        <div 
                          v-for="(color, fileName) in fileColorMap" 
                          :key="fileName"
                          class="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold border"
                          :style="{ borderColor: color.border, backgroundColor: color.bg, color: color.border }"
                        >
                            <span class="material-symbols-outlined text-sm">picture_as_pdf</span>
                            <span class="truncate max-w-[180px]">{{ fileName }}</span>
                            <button @click="removeFile(fileName)" class="hover:opacity-75 ml-1" title="Remove PDF">
                                <span class="material-symbols-outlined text-sm">close</span>
                            </button>
                        </div>
                    </div>

                    <!-- Draggable Grid -->
                    <draggable 
                        v-model="pages" 
                        item-key="uid" 
                        class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6"
                        ghost-class="opacity-40"
                        handle=".drag-handle"
                        :animation="200">
                        <template #item="{ element, index }">
                             <div class="thumbnail-card relative group transition-all duration-200 select-none">
                                <div class="h-56 bg-white rounded-xl border-2 cursor-move relative transition-all hover:shadow-xl drag-handle overflow-hidden"
                                    :style="element.color ? { borderColor: element.color.border } : {}">
                                     
                                     <!-- Top Stripe Accent -->
                                     <div v-if="element.color" class="absolute top-0 left-0 right-0 h-1.5 z-10" :style="{ backgroundColor: element.color.stripe }"></div>
                                     
                                     <!-- Page Canvas / Image -->
                                     <div class="absolute inset-0 flex items-center justify-center p-3" :style="element.color ? { backgroundColor: element.color.bg } : { backgroundColor: 'white' }">
                                          <img 
                                             v-show="!element.isLoading && element.thumbnail"
                                             :src="element.thumbnail" 
                                             class="max-h-full max-w-full object-contain shadow-sm rounded transition-transform duration-200"
                                             :style="{ transform: `rotate(${element.rotation}deg)` }"
                                             draggable="false"/>

                                          <div v-if="element.isLoading" class="flex flex-col items-center justify-center">
                                              <div class="animate-spin rounded-full h-8 w-8 border-4 border-[#2EB2A0]/30 border-t-[#2EB2A0] mb-2"></div>
                                              <span class="text-[10px] font-bold text-[#2EB2A0]">Rendering...</span>
                                          </div>
                                     </div>

                                     <!-- Page Controls Overlay -->
                                     <div class="absolute top-3 right-3 flex items-center gap-1 opacity-100 md:opacity-0 group-hover:opacity-100 transition-opacity z-20">
                                         <button @click.stop="rotatePage(element, 90)" class="size-7 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center text-slate-700 hover:bg-[#2EB2A0] hover:text-white shadow transition-colors" title="Rotate">
                                             <span class="material-symbols-outlined text-[16px]">rotate_right</span>
                                         </button>
                                         <button @click.stop="deletePage(element)" class="size-7 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center text-red-500 hover:bg-red-500 hover:text-white shadow transition-colors" title="Delete">
                                             <span class="material-symbols-outlined text-[16px]">delete</span>
                                         </button>
                                     </div>

                                     <!-- Footer Page Label -->
                                     <div class="absolute bottom-0 left-0 right-0 bg-white/90 backdrop-blur-sm border-t border-slate-100 px-3 py-1.5 flex justify-between items-center z-20">
                                         <span class="text-[10px] font-semibold text-slate-500 truncate max-w-[110px]">{{ element.fileName }}</span>
                                         <span class="text-xs font-bold text-[#16304D]">P. {{ element.filePageNum }}</span>
                                     </div>
                                </div>
                             </div>
                        </template>
                    </draggable>
                </div>
            </main>

            <!-- Sidebar (Right - Summary & Actions) -->
            <aside class="w-80 border-l border-slate-200 bg-white flex flex-col shrink-0 z-10 shadow-lg hidden md:flex">
                <div class="p-6 flex flex-col flex-1 gap-6 overflow-y-auto">
                    <div>
                        <h3 class="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">Merge Controls</h3>
                        <div class="flex flex-col gap-3">
                            <button @click="addMoreFiles" class="flex items-center gap-3 px-4 py-3 rounded-xl border border-slate-200 hover:bg-slate-50 hover:border-[#2EB2A0]/50 transition-all font-semibold text-slate-700 text-sm">
                                <span class="material-symbols-outlined text-[#2EB2A0]">add_box</span>
                                Add More PDF Files
                            </button>

                            <button @click="rotateAllPages" class="flex items-center gap-3 px-4 py-3 rounded-xl border border-slate-200 hover:bg-slate-50 hover:border-[#2EB2A0]/50 transition-all font-semibold text-slate-700 text-sm">
                                <span class="material-symbols-outlined text-slate-500">sync</span>
                                Rotate All Pages (90°)
                            </button>

                            <button @click="sortFilesByName" class="flex items-center justify-between px-4 py-3 rounded-xl border border-slate-200 hover:bg-slate-50 transition-all font-semibold text-slate-700 text-sm">
                                <div class="flex items-center gap-3">
                                    <span class="material-symbols-outlined text-slate-500">sort_by_alpha</span>
                                    Sort PDF Alphabetically
                                </div>
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Download Bar -->
                <div class="p-6 bg-slate-50 border-t border-slate-200">
                    <div class="mb-4">
                        <p class="text-xs text-slate-400 font-bold uppercase tracking-wider mb-1">Combined Document</p>
                        <p class="text-xl font-bold text-[#16304D]">{{ pages.length }} Pages <span class="text-slate-400 text-sm font-normal">&middot; {{ formattedTotalSize }}</span></p>
                    </div>

                    <button @click="saveAndDownload" :disabled="isDownloading || pages.length === 0" class="w-full bg-[#2EB2A0] text-white h-12 rounded-xl text-base font-bold flex items-center justify-center gap-2 shadow-lg hover:bg-[#279B8C] transition-all disabled:opacity-50">
                        <span v-if="isDownloading" class="animate-spin material-symbols-outlined">progress_activity</span>
                        <span v-else class="material-symbols-outlined">download</span>
                        <span>{{ isDownloading ? 'Merging PDFs...' : 'Merge & Download PDF' }}</span>
                    </button>
                </div>
            </aside>
        </div>
    </div>

    <!-- Hidden Input -->
    <input type="file" ref="fileInput" multiple accept="application/pdf" class="hidden" @change="onFileChange" />
  </div>
</template>

<script setup>
import { ref, computed, nextTick, shallowRef, markRaw } from 'vue'
import draggable from 'vuedraggable'
import { PDFDocument, degrees } from 'pdf-lib'
import * as pdfjsLib from 'pdfjs-dist/build/pdf'

pdfjsLib.GlobalWorkerOptions.workerSrc = '/js/pdf.worker.js';

const pages = ref([])
const pdfjsDocs = shallowRef({})
const pdfLibDocs = shallowRef({})
const isDragging = ref(false)
const isDownloading = ref(false)
const totalFileSize = ref(0)
const fileInput = ref(null)
const scrollContainer = ref(null)

const FILE_COLOR_PALETTE = [
  { border: '#00BCD4', bg: '#F0FEFF', stripe: '#00BCD4' },
  { border: '#F4811F', bg: '#FFF8F0', stripe: '#F4811F' },
  { border: '#2ECC71', bg: '#F0FFF8', stripe: '#2ECC71' },
  { border: '#E53935', bg: '#FFF5F5', stripe: '#E53935' },
  { border: '#1565C0', bg: '#F0F4FF', stripe: '#1565C0' },
  { border: '#7B1FA2', bg: '#F9F0FF', stripe: '#7B1FA2' }
]
const fileColorMap = ref({})
let fileColorIndex = 0

const getFileColor = (fileName) => {
  if (!fileColorMap.value[fileName]) {
    fileColorMap.value[fileName] = FILE_COLOR_PALETTE[fileColorIndex % FILE_COLOR_PALETTE.length]
    fileColorIndex++
  }
  return fileColorMap.value[fileName]
}

const fileInfo = computed(() => {
  const uniqueFiles = [...new Set(pages.value.map(p => p.fileName))]
  return { fileCount: uniqueFiles.length }
})

const formattedTotalSize = computed(() => {
  if (totalFileSize.value === 0) return '0 KB'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(totalFileSize.value) / Math.log(k))
  return parseFloat((totalFileSize.value / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
})

let uidCounter = 0

const processFiles = async (files) => {
  const pdfFiles = files.filter(f => f.type === 'application/pdf')
  if (!pdfFiles.length) return

  for (const file of pdfFiles) {
    totalFileSize.value += file.size
    const buffer = await file.arrayBuffer()
    const pdfLibDoc = await PDFDocument.load(buffer)
    pdfLibDocs.value[file.name] = markRaw(pdfLibDoc)

    const uint8 = new Uint8Array(buffer.slice(0))
    const pdfjsDoc = await pdfjsLib.getDocument({ data: uint8 }).promise
    pdfjsDocs.value[file.name] = markRaw(pdfjsDoc)

    const newPages = []
    const fileColor = getFileColor(file.name)
    for (let i = 1; i <= pdfjsDoc.numPages; i++) {
      newPages.push({
        uid: uidCounter++,
        fileName: file.name,
        pageNum: i,
        filePageNum: i,
        rotation: 0,
        thumbnail: '',
        isLoading: true,
        color: fileColor
      })
    }

    const startIndex = pages.value.length
    pages.value.push(...newPages)
    const reactiveNewPages = pages.value.slice(startIndex, startIndex + newPages.length)
    renderThumbnailsBackground(pdfjsDoc, reactiveNewPages)
  }
}

const renderThumbnailsBackground = async (pdfjsDoc, pageObjects) => {
  for (const pageObj of pageObjects) {
    try {
      await new Promise(resolve => setTimeout(resolve, 30))
      const pdfPage = await pdfjsDoc.getPage(pageObj.pageNum)
      const viewport = pdfPage.getViewport({ scale: 1.5, rotation: 0 })
      const canvas = document.createElement('canvas')
      canvas.width = viewport.width
      canvas.height = viewport.height
      const ctx = canvas.getContext('2d')
      await pdfPage.render({ canvasContext: ctx, viewport }).promise
      pageObj.thumbnail = canvas.toDataURL('image/png')
      pageObj.isLoading = false
    } catch (e) {
      pageObj.isLoading = false
    }
  }
}

const onFileChange = (e) => {
  const files = Array.from(e.target.files)
  if (files.length) processFiles(files)
  e.target.value = ''
}

const addMoreFiles = () => fileInput.value.click()

const removeFile = (fileName) => {
  pages.value = pages.value.filter(p => p.fileName !== fileName)
  delete fileColorMap.value[fileName]
  delete pdfLibDocs.value[fileName]
  delete pdfjsDocs.value[fileName]
}

const rotatePage = (page, deg) => {
  page.rotation = (page.rotation + deg + 360) % 360
}

const rotateAllPages = () => {
  pages.value.forEach(p => p.rotation = (p.rotation + 90) % 360)
}

const deletePage = (page) => {
  pages.value = pages.value.filter(p => p.uid !== page.uid)
}

const sortFilesByName = () => {
  pages.value.sort((a, b) => a.fileName.localeCompare(b.fileName))
}

const saveAndDownload = async () => {
  try {
    isDownloading.value = true
    await nextTick()
    const mergedPdf = await PDFDocument.create()
    for (const page of pages.value) {
      const origPdf = pdfLibDocs.value[page.fileName]
      const [copiedPage] = await mergedPdf.copyPages(origPdf, [page.pageNum - 1])
      if (page.rotation !== 0) copiedPage.setRotation(degrees(page.rotation))
      mergedPdf.addPage(copiedPage)
    }
    const bytes = await mergedPdf.save()
    const blob = new Blob([bytes], { type: 'application/pdf' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `merged-document-${Date.now()}.pdf`
    a.click()
    URL.revokeObjectURL(url)
  } catch (e) {
    console.error(e)
    alert('Error merging PDFs: ' + e.message)
  } finally {
    isDownloading.value = false
  }
}

const resetAll = () => {
  pages.value = []
  pdfjsDocs.value = {}
  pdfLibDocs.value = {}
  fileColorMap.value = {}
  totalFileSize.value = 0
}

const onDragEnter = (e) => { e.preventDefault(); isDragging.value = true; }
const onDragLeave = (e) => { e.preventDefault(); isDragging.value = false; }
const onDragOver = (e) => { e.preventDefault(); }
const onDrop = (e) => {
  e.preventDefault()
  isDragging.value = false
  if (e.dataTransfer.files.length) processFiles(Array.from(e.dataTransfer.files))
}
</script>
